import { useEffect, useState } from 'react';
import { api, saveSession, type Session } from './api.js';
import { generateDeviceKeys } from './crypto/engine.js';
import { takeLogoutReason } from './api.js';
import { LockMark } from './ui.js';
import { useT } from './i18n.js';

/**
 * Anmeldung mit Name und Passwort.
 *
 * Der Einmal-Code als zweiter Weg wurde auf Wunsch entfernt. Die Route
 * `POST /v1/provision` im Server besteht weiter und funktioniert; sie ist nur
 * aus der Oberflaeche nicht mehr erreichbar. Wer sie zurueckholt, braucht hier
 * wieder ein Eingabefeld und `api.provision()`.
 *
 * `generateDeviceKeys()` erzeugt Identity Key, Signed Prekey und 100
 * One-Time-Prekeys LOKAL im Browser. An den Server gehen ausschliesslich die
 * oeffentlichen Teile. Der Admin, der Konto und Passwort vergeben hat, bekommt
 * die privaten Schluessel nie zu sehen.
 */

export function Login({ onDone }: { onDone: (session: Session) => void }) {
  const t = useT();
  const [handle, setHandle] = useState('');

  /**
   * Selbstregistrierung — nur sichtbar, wenn der Server sie erlaubt.
   *
   * `null` heisst "noch nicht bekannt": Solange die Antwort aussteht, wird
   * nichts angeboten. Das ist besser, als kurz einen Knopf zu zeigen, der
   * gleich wieder verschwindet.
   */
  // Unfreiwillig abgemeldet? Dann steht hier, warum — einmalig.
  const [notice] = useState<string | null>(() => {
    const reason = takeLogoutReason();
    return reason === 'device_removed' ? 'device_removed' : null;
  });
  const [canRegister, setCanRegister] = useState<boolean | null>(null);
  const [registering, setRegistering] = useState(false);
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Zweiter Schritt, solange das Startpasswort des Admins noch gilt.
  const [mustChange, setMustChange] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');

  function describe(message: string): string {
    if (message.includes('invalid_credentials')) return t('errInvalidCredentials');
    if (message.includes('identifier_taken')) return t('errIdentifierTaken');
    if (message.includes('not_found')) return t('errRegistrationClosed');
    if (message.includes('password_unchanged')) return t('errPasswordUnchanged');
    if (message.includes('account_not_active')) return t('errAccountSuspended');
    return message;
  }

  /** Schlüssel erzeugen und Gerät registrieren — der gemeinsame Schlussteil. */
  async function setUpDevice(token: string, name: string) {
    setStatus(t('generatingKeys'));
    const keys = await generateDeviceKeys();

    setStatus(t('registeringDevice'));
    const device = await api.registerDevice(token, {
      name: navigator.userAgent.slice(0, 60),
      identityKey: keys.identityKey,
      registrationId: keys.registrationId,
      signedPreKey: keys.signedPreKey,
      oneTimePreKeys: keys.oneTimePreKeys,
    });

    const session: Session = {
      token: device.token,
      accountId: device.accountId,
      deviceId: device.deviceId,
      handle: name,
    };
    saveSession(session);
    onDone(session);
  }

  useEffect(() => {
    api
      .features()
      .then((f) => setCanRegister(f.openRegistration))
      // Bei einem Fehler nichts anbieten: Ein Formular, das am Ende doch
      // abgewiesen wird, ist schlechter als keines.
      .catch(() => setCanRegister(false));
  }, []);

  async function submitRegister(event: React.FormEvent) {
    event.preventDefault();
    setError(null);

    if (password.length < 8) {
      setError(t('errPasswordTooShort'));
      return;
    }

    try {
      setStatus(t('creatingAccount'));
      const result = await api.register(email.trim(), displayName.trim(), password);
      await setUpDevice(result.token, result.handle);
    } catch (err) {
      setError(describe((err as Error).message));
      setStatus(null);
    }
  }

  async function submitPassword(event: React.FormEvent) {
    event.preventDefault();
    setError(null);

    try {
      setStatus(t('signingIn'));
      const result = await api.login(handle.trim(), password);

      if (result.mustChangePassword) {
        // Bewusst kein Weiterkommen ohne Wechsel: Solange das vom Admin
        // vergebene Passwort gilt, könnte er sich selbst anmelden, ein Gerät
        // registrieren und ab dann mitlesen. Der Server lehnt die
        // Registrierung in diesem Zustand ohnehin ab.
        setMustChange(true);
        setStatus(null);
        return;
      }

      await setUpDevice(result.token, result.handle);
    } catch (err) {
      setError(describe((err as Error).message));
      setStatus(null);
    }
  }

  async function submitNewPassword(event: React.FormEvent) {
    event.preventDefault();
    setError(null);

    if (newPassword.length < 8) {
      setError(t('errPasswordTooShort'));
      return;
    }
    if (newPassword !== repeatPassword) {
      setError(t('errPasswordsDiffer'));
      return;
    }

    try {
      setStatus(t('changingPassword'));
      await api.changePassword(handle.trim(), password, newPassword);

      const result = await api.login(handle.trim(), newPassword);
      await setUpDevice(result.token, result.handle);
    } catch (err) {
      setError(describe((err as Error).message));
      setStatus(null);
    }
  }

  /* ── Zweiter Schritt: Passwort ändern ─────────────────────────────────── */

  if (mustChange) {
    return (
      <div className="auth">
        <div className="auth-box">
          <span className="auth-mark">
            <LockMark />
          </span>
          <h1>{t('changePassword')}</h1>
          <p className="auth-lead">
            {t('changePasswordLead')}
          </p>

          <form onSubmit={submitNewPassword}>
            <label className="label" htmlFor="new-pw">
              {t('newPassword')}
            </label>
            <input
              id="new-pw"
              className="input"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              disabled={status !== null}
            />

            <label className="label" htmlFor="repeat-pw" style={{ marginTop: '0.75rem' }}>
              Wiederholen
            </label>
            <input
              id="repeat-pw"
              className="input"
              type="password"
              value={repeatPassword}
              onChange={(e) => setRepeatPassword(e.target.value)}
              autoComplete="new-password"
              disabled={status !== null}
            />

            <button
              className="btn"
              style={{ width: '100%', marginTop: '1rem' }}
              type="submit"
              disabled={status !== null}
            >
              {status ?? t('saveAndContinue')}
            </button>
          </form>

          {error && (
            <div className="alert alert-error" style={{ marginTop: '0.9rem' }}>
              {error}
            </div>
          )}
        </div>
      </div>
    );
  }

  /* ── Erster Schritt ───────────────────────────────────────────────────── */

  return (
    <div className="auth">
      <div className="auth-box">
        <span className="auth-mark">
          <LockMark />
        </span>
        <h1>Webmessenger</h1>

        {notice === 'device_removed' && (
          <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
            {t('deviceRemoved')}
          </div>
        )}

        {registering ? (
          <form onSubmit={submitRegister}>
            <label className="label" htmlFor="email">
              {t('email')}
            </label>
            <input
              id="email"
              className="input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@beispiel.de"
              autoComplete="email"
              autoCapitalize="off"
              spellCheck={false}
              disabled={status !== null}
            />

            <label className="label" htmlFor="display" style={{ marginTop: '0.75rem' }}>
              {t('displayName')}
            </label>
            <input
              id="display"
              className="input"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="z. B. Anna"
              autoComplete="nickname"
              disabled={status !== null}
            />
            {/* Getrennt von der E-Mail, weil die Adresse nur als gepepperter
                Hash gespeichert wird und nirgends im Klartext auftauchen soll —
                auch nicht als Teil eines Namens. */}
            <p className="hint" style={{ margin: '0.35rem 0 0' }}>
              {t('displayNameHint')}
            </p>

            <label className="label" htmlFor="newpw" style={{ marginTop: '0.75rem' }}>
              {t('password')}
            </label>
            <input
              id="newpw"
              className="input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              disabled={status !== null}
            />

            <button
              className="btn"
              style={{ width: '100%', marginTop: '1rem' }}
              type="submit"
              disabled={
                !email.trim() || !displayName.trim() || password.length < 8 || status !== null
              }
            >
              {status ?? t('createAccount')}
            </button>

            <button
              type="button"
              className="btn btn-ghost"
              style={{ width: '100%', marginTop: '0.5rem' }}
              onClick={() => {
                setRegistering(false);
                setError(null);
              }}
              disabled={status !== null}
            >
              {t('backToSignIn')}
            </button>
          </form>
        ) : (
        <form onSubmit={submitPassword}>
          <label className="label" htmlFor="handle">
            {t('username')}
          </label>
          <input
            id="handle"
            className="input"
            value={handle}
            onChange={(e) => setHandle(e.target.value)}
            placeholder="z. B. anna"
            autoComplete="username"
            autoCapitalize="off"
            spellCheck={false}
            disabled={status !== null}
          />

          <label className="label" htmlFor="pw" style={{ marginTop: '0.75rem' }}>
            {t('password')}
          </label>
          <input
            id="pw"
            className="input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            disabled={status !== null}
          />

          <button
            className="btn"
            style={{ width: '100%', marginTop: '1rem' }}
            type="submit"
            disabled={!handle.trim() || !password || status !== null}
          >
            {status ?? t('signIn')}
          </button>

          {canRegister === true && (
            <button
              type="button"
              className="btn btn-ghost"
              style={{ width: '100%', marginTop: '0.5rem' }}
              onClick={() => {
                setRegistering(true);
                setError(null);
                setPassword('');
              }}
              disabled={status !== null}
            >
              {t('createAccount')}
            </button>
          )}
        </form>
        )}

        {error && (
          <div className="alert alert-error" style={{ marginTop: '0.9rem' }}>
            {error}
          </div>
        )}

      </div>
    </div>
  );
}
